import { useState, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useRoleDefaults } from '../../hooks/useRoleDefaults'
import { usePermission } from '../../../hooks/usePermission'
import { useToast } from '../../hooks/useToast'
import { parseApiError } from '../../utils/error-parser'
import { api } from '../../../services/api'

const ROLE_ORDER = ['member', 'approver', 'procurement', 'admin'] as const

interface CapForm {
  basket_value_limit: string
  weekly_order_limit: string
}

function parseCapValue(raw: string | null): string {
  if (!raw) return ''
  const n = parseFloat(raw)
  return isNaN(n) ? '' : String(n)
}

export function SpendCapsSettings() {
  const { addToast } = useToast()
  const queryClient = useQueryClient()
  const { data: roles, isLoading } = useRoleDefaults()
  const { can } = usePermission()
  const canEdit = can('manage_settings')

  const [forms, setForms] = useState<Record<string, CapForm>>({})
  const [saving, setSaving] = useState<Record<string, boolean>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  // Tracks which role slugs the user has edited but not yet saved, so that a
  // background refetch after saving one card does not overwrite pending edits
  // in the other cards.
  const [dirtySlug, setDirtySlug] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!roles) return
    setForms((prev) => {
      const next = { ...prev }
      roles.forEach((role) => {
        if (!dirtySlug.has(role.slug)) {
          next[role.slug] = {
            basket_value_limit: parseCapValue(role.basket_value_limit),
            weekly_order_limit: parseCapValue(role.weekly_order_limit),
          }
        }
      })
      return next
    })
    // dirtySlug intentionally excluded — we only want to sync clean roles
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles])

  const handleChange = (slug: string, field: keyof CapForm, value: string) => {
    setForms((prev) => ({ ...prev, [slug]: { ...prev[slug], [field]: value } }))
    setDirtySlug((prev) => new Set(prev).add(slug))
    setErrors((prev) => { const n = { ...prev }; delete n[`${slug}_${field}`]; return n })
  }

  const validateCap = (slug: string, field: keyof CapForm, label: string): boolean => {
    const raw = forms[slug]?.[field] ?? ''
    if (raw === '') return true
    const n = parseFloat(raw)
    if (isNaN(n) || n < 0) {
      setErrors((prev) => ({ ...prev, [`${slug}_${field}`]: `${label} must be a positive number.` }))
      return false
    }
    return true
  }

  const handleSave = async (slug: string) => {
    const basketOk = validateCap(slug, 'basket_value_limit', 'Basket limit')
    const weeklyOk = validateCap(slug, 'weekly_order_limit', 'Weekly limit')
    if (!basketOk || !weeklyOk) return

    const form = forms[slug]
    const payload: { basket_value_limit: number | null; weekly_order_limit: number | null } = {
      basket_value_limit: form.basket_value_limit !== '' ? parseFloat(form.basket_value_limit) : null,
      weekly_order_limit: form.weekly_order_limit !== '' ? parseFloat(form.weekly_order_limit) : null,
    }

    setSaving((prev) => ({ ...prev, [slug]: true }))
    try {
      await api.updateRoleDefaults(slug, payload)
      // Clear dirty flag before invalidating so the refetch syncs this slug
      // from the server without touching other in-progress role forms.
      setDirtySlug((prev) => { const next = new Set(prev); next.delete(slug); return next })
      await queryClient.invalidateQueries({ queryKey: ['role-defaults'] })
      addToast('Spend caps saved', 'success', 2000)
    } catch (err) {
      const parsed = parseApiError(err)
      addToast(parsed.message, 'error')
    } finally {
      setSaving((prev) => ({ ...prev, [slug]: false }))
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary" />
      </div>
    )
  }

  const roleMap = Object.fromEntries((roles ?? []).map((r) => [r.slug, r]))

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Spend caps</h1>
      <p className="mt-1 sm:mt-2 text-sm text-gray-600">
        Set basket and weekly spend limits per role. Orders exceeding a cap are routed for approval.
        Leave a field blank to remove the cap for that role.
      </p>

      {!canEdit && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-700">You have view-only access to settings.</p>
        </div>
      )}

      <div className="mt-6 sm:mt-8 space-y-4">
        {ROLE_ORDER.map((slug) => {
          const role = roleMap[slug]
          if (!role) return null
          const form = forms[slug] ?? { basket_value_limit: '', weekly_order_limit: '' }
          const isSaving = saving[slug] ?? false
          const basketError = errors[`${slug}_basket_value_limit`]
          const weeklyError = errors[`${slug}_weekly_order_limit`]

          return (
            <div key={slug} className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{role.name}</h3>
                  {!role.has_tenant_override && (
                    <span className="text-xs text-gray-400">Using system defaults</span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Basket value limit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Basket limit (£)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="No limit"
                    disabled={!canEdit}
                    value={form.basket_value_limit}
                    onChange={(e) => handleChange(slug, 'basket_value_limit', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary ${
                      basketError ? 'border-red-400' : 'border-gray-300'
                    } ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                  />
                  {basketError && <p className="mt-1 text-xs text-red-600">{basketError}</p>}
                  <p className="mt-1 text-xs text-gray-400">
                    Orders over this amount require approval
                  </p>
                </div>

                {/* Weekly order limit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Weekly limit (£)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="No limit"
                    disabled={!canEdit}
                    value={form.weekly_order_limit}
                    onChange={(e) => handleChange(slug, 'weekly_order_limit', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary ${
                      weeklyError ? 'border-red-400' : 'border-gray-300'
                    } ${!canEdit ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
                  />
                  {weeklyError && <p className="mt-1 text-xs text-red-600">{weeklyError}</p>}
                  <p className="mt-1 text-xs text-gray-400">
                    Cumulative spend per week before approval is triggered
                  </p>
                </div>
              </div>

              {canEdit && (
                <div className="mt-4 flex justify-end">
                  <button
                    type="button"
                    disabled={isSaving}
                    onClick={() => handleSave(slug)}
                    className="px-4 py-2 bg-brand-primary text-[var(--brand-primary-text,#fff)] text-sm font-medium rounded-lg hover:bg-brand-primary-hover disabled:opacity-50 transition-colors"
                  >
                    {isSaving ? 'Saving…' : 'Save'}
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
